import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import InputForm from 'components/InputForm';
import SelectForm from 'components/SelectForm';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FaEdit } from 'react-icons/fa';
import { updateRecurrenceClient } from 'services/api.service';
import { RecurrencyPeriod, RecurrencyPeriodDescription } from './recurrency-type';

interface EditForm {
  initDate: string;
  endDate: string;
  nextDate: string;
  lastDate: string;
  period: string;
}

interface EditRecurrenceClientProps {
  recurrenceClient: {
    id: string;
    initDate: string;
    endDate: string | null;
    nextDate: string | null;
    lastDate: string | null;
    period: string;
  };
  recurrenceId: string;
}

const EditRecurrenceClient: React.FC<EditRecurrenceClientProps> = ({ recurrenceClient, recurrenceId }) => {
  const toast = useToast();
  const [loading, setLoading] = React.useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { handleSubmit, control, reset } = useForm<EditForm>();

  const save = async (dataForm: EditForm) => {
    setLoading(true);
    updateRecurrenceClient(recurrenceClient.id, {
      initDate: dataForm.initDate || undefined,
      endDate: dataForm.endDate || undefined,
      nextDate: dataForm.nextDate || undefined,
      lastDate: dataForm.lastDate || undefined,
      period: dataForm.period || undefined,
    })
      .then(() => {
        onClose();
        queryClient.refetchQueries({
          queryKey: ['getRecurrence', recurrenceId],
          type: 'active',
        });
        toast({
          title: 'Sucesso',
          description: 'Cliente atualizado com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((err) => {
        toast({
          title: 'Erro',
          description: err?.response?.data?.message || 'Erro ao atualizar cliente',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        initDate: recurrenceClient.initDate
          ? DateTime.fromISO(recurrenceClient.initDate).toFormat('yyyy-MM-dd')
          : '',
        endDate: recurrenceClient.endDate
          ? DateTime.fromISO(recurrenceClient.endDate).toFormat('yyyy-MM-dd')
          : '',
        nextDate: recurrenceClient.nextDate
          ? DateTime.fromISO(recurrenceClient.nextDate).toFormat('yyyy-MM-dd')
          : '',
        lastDate: recurrenceClient.lastDate
          ? DateTime.fromISO(recurrenceClient.lastDate).toFormat('yyyy-MM-dd')
          : '',
        period: recurrenceClient.period,
      });
    }
  }, [isOpen, recurrenceClient, reset]);

  return (
    <>
      <IconButton
        aria-label="Editar cliente"
        icon={<FaEdit />}
        onClick={onOpen}
        size="sm"
        variant="outline"
        colorScheme="brand"
      />

      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Editar datas do cliente</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={3}>
                <SelectForm
                  control={control}
                  name="period"
                  label="Período"
                  options={Object.values(RecurrencyPeriod).map((period) => ({
                    label: RecurrencyPeriodDescription[period],
                    value: period,
                  }))}
                />
                <InputForm
                  control={control}
                  name="initDate"
                  label="Data de início"
                  type="date"
                />
                <InputForm
                  control={control}
                  name="lastDate"
                  label="Última cobrança"
                  type="date"
                />
                <InputForm
                  control={control}
                  name="nextDate"
                  label="Próxima cobrança"
                  type="date"
                />
                <InputForm
                  control={control}
                  name="endDate"
                  label="Data de fim"
                  type="date"
                />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <HStack spacing={4}>
                <Button colorScheme="red" isLoading={loading} onClick={onClose}>
                  Cancelar
                </Button>
                <Button colorScheme="green" isLoading={loading} onClick={handleSubmit(save)}>
                  Salvar
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default EditRecurrenceClient;
